"use client";

import { Alert } from "@/components/ui/alert";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AIChatComposer from "@/components/AIChatComposer";
import AIChatHeader from "@/components/AIChatHeader";
import AIChatMessage, { AIChatThinking } from "@/components/AIChatMessage";
import AIChatSuggestedPrompts from "@/components/AIChatSuggestedPrompts";
import { useCoachPane } from "@/components/nav/NavCoachPane";
import { NAV_ARRIVE, arriveStep } from "@/components/nav/nav-styles";
import { Badge } from "@/components/ui/badge";
import SmallLoadingSpinner from "@/components/SmallLoadingSpinner";
import AIChatCopyButton from "@/components/AIChatCopyButton";
import { cn } from "@/lib/utils";
import { useSubmit } from "@/hooks/useSubmit";
import { useShareChat } from "@/hooks/useShareChat";
import { chatFeedbackContext } from "@/lib/ai-chat-subject";
import { getChatMessageKey } from "@/lib/ai-chat-feedback-utils";
import { getMessageTimestampMs } from "@/lib/ai-message-utils";
import { formatRelativeTime } from "@/lib/format-date";

// Re-exported so existing `import { AIChatCopyButton } from "@/components/AIChat"`
// callers keep working; the component itself lives in its own file.
export { AIChatCopyButton };

/**
 * Sabine's chat: the thread, the suggested prompts and the composer, filling
 * whatever holds it. Pages register it with the side nav (`SideNavSlot
 * coach`), which shows it in the coach pane beside the icon rail; the pane
 * tells it, through `useCoachPane`, whether it is showing and which header
 * controls (close, fullscreen) to offer. Rendered anywhere else it fills its
 * parent and shows neither.
 *
 * @param {Object} props - Component props
 * @param {Array} props.messages - Array of message objects with id, role, content
 * @param {boolean} props.isProcessing - Whether AI is currently processing
 * @param {string} props.inputValue - Current input field value
 * @param {Function} props.onInputChange - Callback for input field changes
 * @param {Function} props.setInputValue - Optional state setter for input value (used by voice input)
 * @param {Function} props.onSubmit - Callback for form submission (receives enableWebSearch flag)
 * @param {Array} props.prompts - Suggested prompt options (string or { label, prompt })
 * @param {Function} props.onPromptSelect - Callback when a suggested prompt is selected
 * @param {Array} props.summaryChips - Array of summary chip objects with label and value
 * @param {Array} [props.lessons] - Workspace lessons the coach has in context, with id, title, url
 * @param {boolean} [props.contextLoading] - Whether context is being loaded
 * @param {string} props.error - Error message if any
 * @param {Function} props.onReset - Callback for reset button
 * @param {boolean} props.showWebSearchToggle - Whether to show web search toggle (optional)
 * @param {Function} props.renderMessageContent - Optional renderer for message body content
 * @param {Function} props.renderProcessingContent - Optional renderer for processing state content
 * @param {import("@/lib/ai-chat-subject").ChatSubject} [props.subject] - What the chat is about: where its feedback is filed and, for a chat about a record, what a shared copy links back to
 * @param {string} props.contextBadge - Optional short label shown as a pill in the header (e.g. "Analyzing Jane Doe")
 */
export default function AIChat({
  messages,
  isProcessing,
  inputValue,
  onInputChange,
  setInputValue,
  onSubmit,
  prompts,
  onPromptSelect,
  summaryChips,
  lessons,
  contextLoading,
  error,
  onReset,
  showWebSearchToggle = false,
  renderMessageContent,
  renderProcessingContent,
  subject = {},
  // False when the caller renders its own thumbs inside renderMessageContent
  // (the playbook does).
  showFeedback = true,
  showCopyAction = true,
  contextBadge = null,
  inputPlaceholder = "Ask for coaching…",
  belowMessagesContent = null,
}) {
  const { open, onClose, isFullscreen, onFullscreenChange, headerSlot } =
    useCoachPane();
  const scrollRef = useRef(null);
  // Enter sends, Shift+Enter starts a new line.
  const { formRef, onKeyDown: submitOnEnter } = useSubmit();
  const [enableWebSearch, setEnableWebSearch] = useState(true);
  const { share, isSharing } = useShareChat({ subject, summaryChips, messages });
  const messageSeenAtRef = useRef(new Map());
  // `now` stays null until after mount so the server and first client render
  // omit relative timestamps identically (no hydration mismatch); the interval
  // then keeps "1m ago / 2m ago" fresh while the panel is open.
  const [now, setNow] = useState(null);

  useEffect(() => {
    setNow(Date.now());
    const intervalId = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(intervalId);
  }, []);

  const getDisplayTimestampMs = useCallback((message, index) => {
    const realTimestamp = getMessageTimestampMs(message);
    if (realTimestamp != null) return realTimestamp;
    const key = getChatMessageKey(message, index);
    const seen = messageSeenAtRef.current;
    if (!seen.has(key)) seen.set(key, Date.now());
    return seen.get(key);
  }, []);

  // Clicking a suggested prompt fills the input (via the parent's
  // onPromptSelect) and then auto-submits once the value lands — so a tap goes
  // straight in without the user pressing send. Works across every parent
  // because we wait for the controlled inputValue to update.
  const pendingPromptSubmitRef = useRef(false);

  useEffect(() => {
    if (!pendingPromptSubmitRef.current) return;
    if (isProcessing || !inputValue?.trim()) return;
    pendingPromptSubmitRef.current = false;
    // Trigger the real form submit so the synthetic event carries a proper
    // `event.target` (the form). Some consumers read `new FormData(event.target)`
    // in their submit handler, which a hand-rolled `{ preventDefault }` breaks.
    formRef.current?.requestSubmit();
  }, [inputValue, isProcessing]);

  const handlePromptClick = (value) => {
    if (isProcessing) return;
    onPromptSelect?.(value);
    pendingPromptSubmitRef.current = true;
  };

  useEffect(() => {
    if (!open) return;
    const container = scrollRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isProcessing, open]);

  const feedbackSource = subject.source || "GENERIC";
  const feedbackContext = chatFeedbackContext(subject);
  const lastAssistantIndex =
    messages
      .map((m, i) => ({ m, i }))
      .filter(({ m }) => m.role === "assistant")
      .at(-1)?.i ?? -1;
  const hasUserMessages = messages.some((m) => m.role === "user");
  // The chips sit under Sabine's opening message, or stand alone when the
  // thread is empty (the home chat opens with nothing said yet).
  const suggestions = !isProcessing && !hasUserMessages && (
    <AIChatSuggestedPrompts
      prompts={prompts}
      disabled={isProcessing}
      onPick={handlePromptClick}
    />
  );

  const header = (
    <AIChatHeader
      ink={Boolean(headerSlot)}
      contextBadge={contextBadge}
      onReset={onReset}
      resetDisabled={isProcessing}
      onShare={subject.kind && hasUserMessages ? share : null}
      isSharing={isSharing}
      shareDisabled={isSharing || isProcessing}
      isFullscreen={isFullscreen}
      onFullscreenChange={onFullscreenChange}
      onClose={onClose}
    />
  );

  return (
    <div
      data-testid="ai-chat-panel"
      className="flex h-full w-full flex-col overflow-hidden"
    >
      {/* Beside the rail the header sits in the navy top bar, right above
          the column (useCoachPane's headerSlot); elsewhere, at its top. */}
      {headerSlot ? createPortal(header, headerSlot) : header}

      {/* Messages Area */}
      {/* Beside the rail the thread and the composer come in after the
          header as the column opens (NAV_ARRIVE). */}
      <div
        ref={scrollRef}
        className={cn(
          NAV_ARRIVE,
          "flex-1 overflow-y-auto px-5 pt-4 space-y-4 w-full flex justify-center overscroll-y-contain",
        )}
        style={arriveStep(1)}
      >
        <div className="max-w-4xl w-full space-y-5">
          {/* Summary Chips */}
          {summaryChips?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {summaryChips.map((chip) => (
                <Badge
                  key={`${chip.label}-${chip.value}`}
                  tone="neutral"
                  size="sm"
                >
                  <span className="font-normal text-gray-500">
                    {chip.label}:
                  </span>
                  &nbsp;{chip.value}
                </Badge>
              ))}
              {contextLoading && (
                <Badge tone="info" variant="soft" size="sm" className="gap-1">
                  <SmallLoadingSpinner size={12} />
                  Syncing context…
                </Badge>
              )}
            </div>
          )}

          {/* Workspace Lessons */}
          {lessons?.length > 0 && (
            <div className="space-y-3">
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-3">
                <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-indigo-700">
                  <span>Workspace Lessons</span>
                  <span className="text-[10px] text-indigo-500">Context</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {lessons.slice(0, 4).map((lesson) => (
                    <a
                      key={lesson.id}
                      href={lesson.url || "#"}
                      target={lesson.url ? "_blank" : undefined}
                      rel={lesson.url ? "noreferrer" : undefined}
                      className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white/80 px-3 py-1 text-xs font-medium text-indigo-700 transition hover:border-indigo-400"
                    >
                      {lesson.title}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.length === 0 && suggestions}

          {messages.map((message, index) => (
            <AIChatMessage
              key={getChatMessageKey(message, index)}
              message={message}
              index={index}
              messages={messages}
              relativeTime={formatRelativeTime(
                getDisplayTimestampMs(message, index),
                now,
              )}
              deferFeedback={isProcessing && index === messages.length - 1}
              source={feedbackSource}
              feedbackContext={feedbackContext}
              showFeedback={showFeedback}
              showCopyAction={showCopyAction}
              renderMessageContent={renderMessageContent}
            >
              {index === lastAssistantIndex && suggestions}
            </AIChatMessage>
          ))}

          {isProcessing && (
            <AIChatThinking renderContent={renderProcessingContent} />
          )}

          {/* Error State */}
          {error && (
            <Alert variant="destructive" data-testid="ai-chat-error">
              {error}
            </Alert>
          )}

          {/* Caller-supplied banners (e.g. playbook sync / empty states) */}
          {belowMessagesContent}
        </div>
      </div>

      <div className={cn(NAV_ARRIVE, "shrink-0")} style={arriveStep(2)}>
        <AIChatComposer
          formRef={formRef}
          onSubmit={onSubmit}
          inputValue={inputValue}
          onInputChange={onInputChange}
          setInputValue={setInputValue}
          onKeyDown={submitOnEnter}
          placeholder={inputPlaceholder}
          isProcessing={isProcessing}
          autoFocus={open}
          showWebSearchToggle={showWebSearchToggle}
          enableWebSearch={enableWebSearch}
          onToggleWebSearch={() => setEnableWebSearch((value) => !value)}
        />
      </div>
    </div>
  );
}
